import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { settingsInput } from '@redirector/shared';
import { apiGet, apiSend } from '../lib/api.js';
import { Button } from '../ui/Button.js';
import { Callout } from '../ui/Callout.js';
import { Field } from '../ui/Field.js';
import { useAuth } from '../auth/useAuth.js';

type Settings = {
  globalFallbackUrl: string | null;
  minuteRetentionDays: number;
  hourRetentionDays: number;
  splitDeltaTolerancePct: number;
};

const INPUT =
  'w-full rounded-(--radius-md) border border-rule-strong bg-surface-raised px-(--space-3) py-(--space-2) text-ink';

export function SettingsScreen() {
  const { user } = useAuth();
  const canEdit = user?.role === 'admin';
  const client = useQueryClient();

  const query = useQuery({
    queryKey: ['settings'],
    queryFn: () => apiGet<{ settings: Settings }>('/api/settings'),
  });

  const [fields, setFields] = useState<Settings | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (query.data !== undefined) setFields(query.data.settings);
  }, [query.data]);

  useEffect(() => {
    if (!saved) return;
    const timer = setTimeout(() => setSaved(false), 2_500);
    return () => clearTimeout(timer);
  }, [saved]);

  const save = useMutation({
    mutationFn: (input: Settings) => apiSend<{ settings: Settings }>('PUT', '/api/settings', input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['settings'] });
      setSaved(true);
    },
  });

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (fields === null) return;

    // The very schema the server validates with: a rule that lived only here
    // would be a rule the API does not actually enforce.
    const parsed = settingsInput.safeParse({
      ...fields,
      globalFallbackUrl: fields.globalFallbackUrl ?? '',
    });
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        next[String(issue.path[0] ?? 'root')] ??= issue.message;
      }
      setErrors(next);
      return;
    }

    setErrors({});
    try {
      await save.mutateAsync(parsed.data);
    } catch (error) {
      setErrors({ root: error instanceof Error ? error.message : 'Could not save settings' });
    }
  }

  return (
    <section
      aria-labelledby="settings-heading"
      className="flex max-w-[46rem] flex-col gap-(--space-5)"
    >
      <header>
        <p className="text-[length:var(--text-micro)] uppercase tracking-[0.18em] text-ink-subtle">
          Configuration
        </p>
        <h1 id="settings-heading" className="mt-(--space-1) text-[length:var(--text-display)]">
          Settings
        </h1>
        {!canEdit && (
          <p className="mt-(--space-2) text-ink-muted">
            These are read-only for your account. An administrator can change them.
          </p>
        )}
      </header>

      {errors.root !== undefined && <Callout tone="danger">{errors.root}</Callout>}
      {saved && <Callout tone="ok">Saved. Every replica picked up the change immediately.</Callout>}

      {fields !== null && (
        <form onSubmit={submit} className="flex flex-col gap-(--space-4)" noValidate>
          <Field
            label="Global fallback URL"
            error={errors.globalFallbackUrl}
            hint="Where traffic goes when the hostname is unknown, or when a domain has no fallback of its own. Leave empty to return a 404 instead."
          >
            {({ id, describedBy, invalid }) => (
              <input
                id={id}
                disabled={!canEdit}
                spellCheck={false}
                className={`${INPUT} font-mono text-[length:var(--text-small)]`}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                value={fields.globalFallbackUrl ?? ''}
                onChange={(event) =>
                  setFields({ ...fields, globalFallbackUrl: event.target.value })
                }
              />
            )}
          </Field>

          <Field
            label="Per-minute retention (days)"
            error={errors.minuteRetentionDays}
            hint="How long minute-level traffic is kept before being rolled up into hours."
          >
            {({ id, describedBy, invalid }) => (
              <input
                id={id}
                type="number"
                min={1}
                max={90}
                disabled={!canEdit}
                className={INPUT}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                value={fields.minuteRetentionDays}
                onChange={(event) =>
                  setFields({ ...fields, minuteRetentionDays: Number(event.target.value) })
                }
              />
            )}
          </Field>

          <Field
            label="Hourly retention (days)"
            error={errors.hourRetentionDays}
            hint="How long hour-level traffic is kept before being rolled up into days."
          >
            {({ id, describedBy, invalid }) => (
              <input
                id={id}
                type="number"
                min={1}
                max={1095}
                disabled={!canEdit}
                className={INPUT}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                value={fields.hourRetentionDays}
                onChange={(event) =>
                  setFields({ ...fields, hourRetentionDays: Number(event.target.value) })
                }
              />
            )}
          </Field>

          <Field
            label="Split drift tolerance (%)"
            error={errors.splitDeltaTolerancePct}
            hint="How far the actual share may differ from the configured share before Reports flags it."
          >
            {({ id, describedBy, invalid }) => (
              <input
                id={id}
                type="number"
                min={0}
                max={50}
                step={0.1}
                disabled={!canEdit}
                className={INPUT}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                value={fields.splitDeltaTolerancePct}
                onChange={(event) =>
                  setFields({ ...fields, splitDeltaTolerancePct: Number(event.target.value) })
                }
              />
            )}
          </Field>

          {canEdit && (
            <div className="flex justify-end">
              <Button type="submit" variant="primary" disabled={save.isPending}>
                {save.isPending ? 'Saving…' : 'Save settings'}
              </Button>
            </div>
          )}
        </form>
      )}
    </section>
  );
}
